import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import * as Papa from 'papaparse';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ContactGrouopService } from 'src/app/private/services/contact-groups.service';
import { ToastrService } from 'ngx-toastr';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';

@Component({
  selector: 'app-csv-dropzone',
  templateUrl: './csv-dropzone.component.html',
  styleUrls: ['./csv-dropzone.component.scss']
})
export class CsvDropzoneComponent implements OnInit {
  csvUploaded = false;
  columns: string[] = [];
  csvData: any[] = [];
  mappedData: any[] = [];
  form: FormGroup;
  btnLoading = false;
  
  // Validation preview properties
  showPreview = false;
  validationResults: any[] = [];
  hasValidationErrors = false;

  @Output() formSubmit: EventEmitter<any> = new EventEmitter();
  @Output() close: EventEmitter<void> = new EventEmitter();
  @Input() selectedGroupId: any;
  @Input() campaignType: 'email' | 'whatsapp' | 'both' = 'both';
  @Input() maxContactLimit: number = 1000;

  constructor(
    private fb: FormBuilder,
    private api: ContactGrouopService,
    private toast: ToastrService,
    private localStorage: LocalStorageService,
  ) {
    this.form = this.fb.group({
      username: ['', Validators.required],
      email: [''],
      phone: ['']
    });
  }

  ngOnInit() {
    this.updateFormValidators();
  }

  private updateFormValidators() {
    const emailControl = this.form.get('email');
    const phoneControl = this.form.get('phone');

    if (this.campaignType === 'email') {
      emailControl?.setValidators([Validators.required]);
      phoneControl?.clearValidators();
    } else if (this.campaignType === 'whatsapp') {
      phoneControl?.setValidators([Validators.required]);
      emailControl?.clearValidators();
    } else {
      emailControl?.setValidators([Validators.required]);
      phoneControl?.setValidators([Validators.required]);
    }

    emailControl?.updateValueAndValidity();
    phoneControl?.updateValueAndValidity();
  }

  onFileSelect(event: any) {
    const file = event.addedFiles[0];

    if (file) {
      this.parseCSV(file);
      this.csvUploaded = true;
    }
  }

  parseCSV(file: File) {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        if (result.data.length > this.maxContactLimit) {
          this.toast.error(`File contains ${result.data.length} contacts. Maximum allowed is ${this.maxContactLimit}.`, 'Upload Limit Exceeded');
          this.csvUploaded = false;
          return;
        }

        this.csvData = result.data;
        this.columns = Object.keys(this.csvData[0]);
      }
    });
  }

  onColumnChange() {
    const selectedUsername = this.form.get('username')?.value;
    const selectedEmail = this.form.get('email')?.value;
    const selectedPhone = this.form.get('phone')?.value;

    let canPreview = selectedUsername;
    
    if (this.campaignType === 'email') {
      canPreview = canPreview && selectedEmail;
    } else if (this.campaignType === 'whatsapp') {
      canPreview = canPreview && selectedPhone;
    } else {
      canPreview = canPreview && selectedEmail && selectedPhone;
    }

    if (canPreview) {
      this.previewAndValidate();
    } else {
      this.showPreview = false;
    }
  }

  previewAndValidate() {
    const selectedUsername = this.form.get('username')?.value;
    const selectedEmail = this.form.get('email')?.value;
    const selectedPhone = this.form.get('phone')?.value;

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const phoneRegex = /^[0-9]{10,15}$/;

    this.validationResults = this.csvData
      .map((row, index) => {
        const name = row[selectedUsername];
        const email = selectedEmail ? row[selectedEmail] : null;
        const phone = selectedPhone ? row[selectedPhone] : null;

        const hasName = name && name.toString().trim() !== '';
        const hasEmail = email && email.toString().trim() !== '';
        const hasPhone = phone && phone.toString().trim() !== '';
        const isEmptyRow = !hasName && !hasEmail && !hasPhone;

        if (isEmptyRow) {
          return null;
        }

        let emailValid = true;
        let emailError = '';
        
        if (this.campaignType === 'email' || this.campaignType === 'both') {
          if (!email || email.trim() === '') {
            emailValid = false;
            emailError = 'Missing (required for email campaign)';
          } else if (!emailRegex.test(email.trim())) {
            emailValid = false;
            emailError = 'Invalid format';
          }
        } else {
          if (email && email.trim() !== '') {
            if (!emailRegex.test(email.trim())) {
              emailValid = false;
              emailError = 'Invalid format';
            }
          }
        }

        let phoneValid = true;
        let phoneError = '';
        
        if (this.campaignType === 'whatsapp' || this.campaignType === 'both') {
          if (!phone || phone.toString().trim() === '') {
            phoneValid = false;
            phoneError = 'Missing (required for WhatsApp campaign)';
          } else {
            const cleanPhone = phone.toString().replace(/\s+/g, '');
            if (!phoneRegex.test(cleanPhone)) {
              phoneValid = false;
              phoneError = 'Invalid format (use 10-15 digits)';
            }
          }
        } else {
          if (phone && phone.toString().trim() !== '') {
            const cleanPhone = phone.toString().replace(/\s+/g, '');
            if (!phoneRegex.test(cleanPhone)) {
              phoneValid = false;
              phoneError = 'Invalid format (use 10-15 digits)';
            }
          }
        }

        return {
          rowIndex: index + 2,
          name: name || 'N/A',
          email: email || 'N/A',
          phone: phone || 'N/A',
          emailValid,
          phoneValid,
          emailError,
          phoneError,
          isValid: emailValid && phoneValid
        };
      })
      .filter(result => result !== null);

    this.hasValidationErrors = this.validationResults.some(r => !r.isValid);
    this.showPreview = true;
  }

  mapData() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const selectedUsername = this.form.get('username')?.value;
    const selectedEmail = this.form.get('email')?.value;
    const selectedPhone = this.form.get('phone')?.value;

    this.mappedData = this.csvData.map((row, index) => ({
      name: row[selectedUsername],
      source: 'csv',
      email: row[selectedEmail],
      phone: row[selectedPhone],
      rowIndex: index + 2
    }));

    this.mappedData = this.mappedData.filter(contact => {
      const hasName = contact.name && contact.name.toString().trim() !== '';
      const hasEmail = contact.email && contact.email.toString().trim() !== '';
      const hasPhone = contact.phone && contact.phone.toString().trim() !== '';
      return hasName || hasEmail || hasPhone;
    });

    if (this.mappedData.length === 0) {
      this.toast.error('No valid contacts found in the CSV file. Please check your file and try again.', 'Empty Contact List');
      return;
    }

    const validationErrors = this.validateContacts(this.mappedData);
    
    if (validationErrors.length > 0) {
      this.showValidationErrors(validationErrors);
      return;
    }

    const cleanedData = this.mappedData.map(({ rowIndex, ...contact }) => contact);

    const payload = {
      hospital_id: this.localStorage.getHospitalId(),
      contact_group_id: this.selectedGroupId,
      contacts: cleanedData
    };
    
    this.btnLoading = true;
    this.api.createContactGroupList(payload).subscribe(
      (response: any) => {
        this.toast.success('Contacts uploaded successfully!');
        this.formSubmit.emit();
      },
      (error: any) => {
        console.log(error);
        this.toast.error("Error while creating contact group");
        this.btnLoading = false;
      }
    );
  }

  private validateContacts(contacts: any[]): string[] {
    const errors: string[] = [];
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const phoneRegex = /^[0-9]{10,15}$/;

    contacts.forEach((contact) => {
      const { email, phone, name, rowIndex } = contact;

      if (this.campaignType === 'email' || this.campaignType === 'both') {
        if (!email || email.trim() === '') {
          errors.push(`Row ${rowIndex}: Email is missing for contact "${name || 'Unknown'}" (required for email campaign)`);
        } else if (!emailRegex.test(email.trim())) {
          errors.push(`Row ${rowIndex}: Invalid email format "${email}" for contact "${name || 'Unknown'}"`);
        }
      } else if (email && email.trim() !== '') {
        if (!emailRegex.test(email.trim())) {
          errors.push(`Row ${rowIndex}: Invalid email format "${email}" for contact "${name || 'Unknown'}"`);
        }
      }

      if (this.campaignType === 'whatsapp' || this.campaignType === 'both') {
        if (!phone || phone.toString().trim() === '') {
          errors.push(`Row ${rowIndex}: Phone number is missing for contact "${name || 'Unknown'}" (required for WhatsApp campaign)`);
        } else {
          const cleanPhone = phone.toString().replace(/\s+/g, '');
          if (!phoneRegex.test(cleanPhone)) {
            errors.push(`Row ${rowIndex}: Invalid phone format "${phone}" for contact "${name || 'Unknown'}". Phone must be 10-15 digits without special characters or + sign.`);
          }
        }
      } else if (phone && phone.toString().trim() !== '') {
        const cleanPhone = phone.toString().replace(/\s+/g, '');
        if (!phoneRegex.test(cleanPhone)) {
          errors.push(`Row ${rowIndex}: Invalid phone format "${phone}" for contact "${name || 'Unknown'}". Phone must be 10-15 digits without special characters or + sign.`);
        }
      }
    });

    return errors;
  }

  private showValidationErrors(errors: string[]) {
    const maxErrorsToShow = 5;
    const errorMessage = errors.slice(0, maxErrorsToShow).join('\n');
    const remainingErrors = errors.length - maxErrorsToShow;
    
    if (remainingErrors > 0) {
      this.toast.error(
        `${errorMessage}\n\n...and ${remainingErrors} more error(s). Please fix all validation errors in your CSV file.`,
        'Validation Failed',
        { timeOut: 10000, enableHtml: true, closeButton: true }
      );
    } else {
      this.toast.error(
        errorMessage,
        'Validation Failed',
        { timeOut: 8000, enableHtml: true, closeButton: true }
      );
    }
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return field!.invalid && field!.touched;
  }

  getValidCount(): number {
    return this.validationResults.filter(r => r.isValid).length;
  }

  getInvalidCount(): number {
    return this.validationResults.filter(r => !r.isValid).length;
  }

  removeInvalidRows() {
    const invalidCount = this.getInvalidCount();
    
    const validIndices = this.validationResults
      .filter(r => r.isValid)
      .map(r => r.rowIndex - 2);

    this.csvData = this.csvData.filter((_, index) => validIndices.includes(index));
    
    this.previewAndValidate();
    
    this.toast.success(`Removed ${invalidCount} invalid row(s)`, 'Cleanup Complete');
  }

  scrollToFirstError() {
    const firstInvalidIndex = this.validationResults.findIndex(r => !r.isValid);
    
    if (firstInvalidIndex !== -1) {
      setTimeout(() => {
        const tableBody = document.querySelector('.preview-table tbody');
        const errorRow = tableBody?.querySelectorAll('tr')[firstInvalidIndex];
        
        if (errorRow) {
          errorRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
          errorRow.classList.add('highlight-error');
          setTimeout(() => {
            errorRow.classList.remove('highlight-error');
          }, 2000);
        }
      }, 100);
    }
  }

  onClose() {
    this.close.emit();
  }
}
